<script setup>
import { cva } from "class-variance-authority";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
const props = defineProps({
  vehicle: {
    type: Object,
    default: void 0
  },
  isStandalone: {
    type: Boolean,
    default: false
  },
  isSelector: {
    type: Boolean,
    default: false
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  isChecked: {
    type: Boolean,
    default: false
  },
  isDisabled: {
    type: Boolean,
    default: false
  },
  customBorderClasses: {
    type: String,
    default: ""
  }
});
const emits = defineEmits(["selectVehicle", "modifyVehicle", "deleteVehicle", "setVehicleAsDefault"]);
const selectVehicle = () => {
  if (!props.vehicle || props.isDisabled) return;
  emits("selectVehicle", props.vehicle.versionId);
};
const { t } = useI18n();
const cardStyles = computed(
  () => cva("group w-full flex 425:h-20 p-4 gap-2 items-center", {
    variants: {
      isStandalone: {
        true: "border rounded-lg 425:rounded-2xl"
      },
      isDefault: {
        true: "border-secondary-gray-disabled"
      },
      isDisabled: {
        true: "border-gray-disabled cursor-not-allowed",
        false: "cursor-pointer"
      },
      isStandaloneSelected: {
        true: "border-light-blue-primary"
      },
      isSelected: {
        true: "bg-primary-light"
      }
    }
  })({
    isStandalone: props.isStandalone,
    isDefault: !props.isSelected && !props.isDisabled,
    isDisabled: props.isDisabled,
    isStandaloneSelected: props.isSelected && props.isStandalone && !props.isDisabled,
    isSelected: props.isSelected && !props.isDisabled
  })
);
const vehicleIconStyles = computed(
  () => cva("flex items-center justify-center size-12 border rounded-md", {
    variants: {
      isDefault: {
        true: "border-secondary-gray-disabled"
      },
      isDisabled: {
        true: "border-gray-disabled"
      },
      isSelected: {
        true: "border-primary-selected"
      }
    }
  })({
    isDefault: !props.isSelected && !props.isDisabled,
    isDisabled: props.isDisabled,
    isSelected: props.isSelected && !props.isDisabled
  })
);
const defaultIconStyles = computed(
  () => cva("size-12 p-2", {
    variants: {
      isDefault: {
        true: "text-icon-secondary-neutral"
      },
      isSelected: {
        true: "text-icon-secondary-branding"
      },
      isDisabled: {
        true: "text-gray-disabled"
      }
    }
  })({
    isDefault: !props.isSelected && !props.isDisabled,
    isSelected: props.isSelected && !props.isDisabled,
    isDisabled: props.isDisabled
  })
);
const vehicleTitleStyles = computed(
  () => cva(
    "text-[15px] 425:text-[17px] font-normal 425:font-medium leading-[18px] 425:leading-5",
    {
      variants: {
        isDefault: {
          true: "text-gray-l-800"
        },
        isDisabled: {
          true: "text-gray-disabled"
        },
        isSelected: {
          true: "text-primary-info"
        }
      }
    }
  )({
    isDefault: !props.isSelected && !props.isDisabled,
    isDisabled: props.isDisabled,
    isSelected: props.isSelected && !props.isDisabled
  })
);
const vehicleVersionStyles = computed(
  () => cva(
    "text-[13px] 425:text-[15px] font-normal leading-4 425:leading-[18px]",
    {
      variants: {
        isDefault: {
          true: "text-gray-l-500"
        },
        isDisabled: {
          true: "text-gray-disabled"
        },
        isSelected: {
          true: "text-light-blue-primary"
        }
      }
    }
  )({
    isDefault: !props.isSelected && !props.isDisabled,
    isDisabled: props.isDisabled,
    isSelected: props.isSelected && !props.isDisabled
  })
);
const selectorIconStyles = computed(
  () => cva("size-5 425:size-6", {
    variants: {
      isSelected: {
        true: !props.isDisabled && "text-icon-secondary-branding",
        false: !props.isDisabled && "text-icon-secondary-neutral"
      },
      isDisabled: {
        true: "text-gray-disabled"
      }
    }
  })({
    isSelected: props.isSelected,
    isDisabled: props.isDisabled
  })
);
const vehicleTitle = computed(() => {
  if (!props.vehicle) {
    return t("simulation.vehicleCell.noVehicle");
  }
  return `${props.vehicle.brandName} ${props.vehicle.modelName}`;
});
</script>

<template>
    <div
        :class="[cardStyles, !isStandalone && customBorderClasses]"
        @click="selectVehicle"
    >
        <div :class="vehicleIconStyles">
            <IconCar :class="defaultIconStyles" />
        </div>

        <div class="flex flex-col 425:gap-0.5 items-start w-full">
            <span :class="vehicleTitleStyles">
                {{ vehicleTitle }}
            </span>
            <span v-if="vehicle" :class="vehicleVersionStyles">
                {{ vehicle.versionName }}
            </span>
        </div>

        <div class="ml-auto">
            <div v-if="isSelector && vehicle" class="flex items-center gap-2">
                <div
                    v-if="!isDisabled && !isChecked"
                    data-testid="vehicle-cell-actions"
                    class="gap-2 flex 425:hidden 425:group-hover:flex items-center"
                >
                    <IconPencil
                        class="size-5 425:size-6 text-icon-secondary-neutral hover:text-icon-secondary-branding"
                        @click.stop="$emit('modifyVehicle', vehicle.versionId)"
                    />
                    <IconTrash
                        v-if="!isSelected"
                        class="size-5 425:size-6 text-icon-secondary-neutral hover:text-icon-secondary-branding"
                        @click.stop="$emit('deleteVehicle', vehicle.versionId)"
                    />
                    <IconCheckCircle
                        class="size-5 425:size-6 text-gray-comment hidden 425:block hover:text-icon-secondary-branding"
                        @click.stop="
  $emit('setVehicleAsDefault', vehicle.versionId)
"
                    />
                </div>
                <IconCheckCircle
                    v-if="isChecked"
                    data-testid="vehicle-cell-check"
                    is-solid
                    class="size-5 425:size-6"
                    :class="
  isDisabled ? 'text-gray-disabled' : 'text-icon-secondary-branding'
"
                />
            </div>
            <IconChevronRight v-else :class="selectorIconStyles" />
        </div>
    </div>
</template>
